import _ from 'lodash';
import { getProductWithQualityAttributes } from './productService';
import { getActiveQualityAttributeValueIdsByMarket } from '../markets/qualityAttributeValueMarketAssignmentService';
import { SHA256 } from "crypto-js";

export function filterProductQualityAttributeValues(product, activeQualityAttributeValueIds) {
  if (!product || !product.qualityAttributeValues) {
    return product;
  }

  if (!activeQualityAttributeValueIds || activeQualityAttributeValueIds.length === 0) {
    // If no active IDs, return product with empty qualityAttributeValues array
    return {
      ...product,
      qualityAttributeValues: []
    };
  }

  // Convert activeQualityAttributeValueIds to a Set for efficient lookup
  const activeIdsSet = new Set(activeQualityAttributeValueIds.map(id => id.toString()));

  // Filter qualityAttributeValues to only include those with active assignments
  const filteredQualityAttributeValues = product.qualityAttributeValues.filter(
    (qav) => activeIdsSet.has(qav.qualityAttributeValueId?.toString())
  );

  return {
    ...product,
    qualityAttributeValues: filteredQualityAttributeValues
  };
}

export function pivotQualityAttributes(product) 
{

  //Get the priceDriver qualityAttributeIds
  const priceDrivers = product.qualityAttributes
  .filter(qualityAttribute => qualityAttribute.isPriceDriver !== false)
  .map(qualityAttribute => (
          qualityAttribute.qualityAttributeId
  ));

  //Group the qualityAttributeValues that are priceDrivers by QualityAttributeId to facilitate the cartesian product
  var qualityAttributes = product.qualityAttributeValues.filter(item => priceDrivers.includes(item.qualityAttributeId));

  //Push the product name onto the array so it's always included in the cartesian (so we alwasy get at least one row)
  qualityAttributes.push({qualityAttributeId:"shortName", value:product.shortName});

  //Create the grouped arrays so the cartesian can be performed
  const groups = _.groupBy(qualityAttributes,  'qualityAttributeId');

  //Set up the cartesian product calculator
  const cartesianProduct = (arrays) => {
      const keys = Object.keys(arrays);

      const product = keys.reduce((a, key) => {
        const values = arrays[key];
        return _.flatMap(a, (x) =>
          values.map((y) => ({ ...x, [y.qualityAttributeId]: {qualityAttributeId: y.qualityAttributeId, qualityAttributeValueId: y.qualityAttributeValueId, value:y.value }}))                
        );
      }, [{}]);

      return product               
         .map((element) => {
          const qualityAttributeValues = keys
         .map((key) => ({
           qualityAttributeId: element[key].qualityAttributeId,
           qualityAttributeValueId: element[key].qualityAttributeValueId,
           value: element[key].value,
         }))
         .filter(x => x.qualityAttributeValueId);

         const elements=Object.fromEntries(
              Object.entries(element).map(([key, value]) => [key, value.value])
          );
        return { ...elements, qualityAttributeValues };
      });
    };

  //Get the cartesian product
  const data = cartesianProduct(groups);

  //Return the cartesian product
  return data;
}

export function getPriceDriverQualityAttributes(product) {
  return (product?.qualityAttributes || []).filter(
    (qualityAttribute) => qualityAttribute.isPriceDriver !== false
  );
}

export function toQualityAttributeValueHash(qualityAttributeValues = []) {
  const sortedQualityAttributeValues = qualityAttributeValues
    .map(
      (qualityAttributeValue) => qualityAttributeValue?.qualityAttributeValueId
    )
    .filter((value) => value !== undefined && value !== null)
    .sort();

  const concatenatedValues = sortedQualityAttributeValues.join("");
  return SHA256(concatenatedValues).toString().toUpperCase();
}

export function buildQualityAttributeValuesByHashMap(rows = []) {
  return rows.reduce((qualityAttributeValuesByHash, row) => {
    qualityAttributeValuesByHash.set(
      row.qualityAttributeValueHash,
      row.qualityAttributeValues || []
    );
    return qualityAttributeValuesByHash;
  }, new Map());
}

export function buildConfiguredPriceDriverCombinationRows(
  product,
  marketPriceDriverCombinations = []
) {
  if (!product) {
    return [];
  }

  const priceDriverQualityAttributes = getPriceDriverQualityAttributes(product);
  const qualityAttributeValuesById = (product.qualityAttributeValues || []).reduce(
    (lookup, qualityAttributeValue) => {
      lookup[qualityAttributeValue.qualityAttributeValueId] = qualityAttributeValue;
      return lookup;
    },
    {}
  );

  const rows = (marketPriceDriverCombinations || [])
    .map((combination) => {
      const qualityAttributeValues = (combination.qualityAttributeValueIds || [])
        .map((qualityAttributeValueId) => qualityAttributeValuesById[qualityAttributeValueId])
        .filter(Boolean);

      if (qualityAttributeValues.length !== (combination.qualityAttributeValueIds || []).length) {
        return null;
      }

      if (
        priceDriverQualityAttributes.length > 0 &&
        qualityAttributeValues.length !== priceDriverQualityAttributes.length
      ) {
        return null;
      }

      const row = {
        combinationId: combination.combinationId,
        isCore: !!combination.isCore,
        shortName: product.shortName,
        qualityAttributeValues,
      };

      priceDriverQualityAttributes.forEach((qualityAttribute) => {
        const matchingValue = qualityAttributeValues.find(
          (qualityAttributeValue) =>
            qualityAttributeValue.qualityAttributeId ===
            qualityAttribute.qualityAttributeId
        );

        row[qualityAttribute.qualityAttributeId] = matchingValue?.value || "";
      });

      row.qualityAttributeValueHash = toQualityAttributeValueHash(
        qualityAttributeValues
      );

      return row;
    })
    .filter(Boolean);

  return rows.sort((left, right) => {
    for (const qualityAttribute of priceDriverQualityAttributes) {
      const leftValue = left[qualityAttribute.qualityAttributeId] || "";
      const rightValue = right[qualityAttribute.qualityAttributeId] || "";
      const comparison = leftValue.localeCompare(rightValue);

      if (comparison !== 0) {
        return comparison;
      }
    }

    return (left.shortName || "").localeCompare(right.shortName || "");
  });
}

/**
 * Fetches product with quality attributes and active quality attribute value IDs for a market,
 * then filters the product to only include active values.
 * @param {string} productId - The product identifier
 * @param {string} marketId - The market identifier
 * @param {string} marketDate - The market date in 'fr-CA' format (YYYY-MM-DD)
 * @returns {Promise<Object>} Object containing originalQualityAttributeValues, filteredProduct, and activeQualityAttributeValueIds
 */
export async function getFilteredProductForMarket(productId, marketId, marketDate) {
  const [product, activeIds] = await Promise.all([
    getProductWithQualityAttributes(productId),
    getActiveQualityAttributeValueIdsByMarket(marketId, marketDate),
  ]);
  
  return {
    originalQualityAttributeValues: product.qualityAttributeValues || [],
    filteredProduct: filterProductQualityAttributeValues(product, activeIds),
    activeQualityAttributeValueIds: activeIds
  };
}

/**
 * Re-filters an existing product for a new market date.
 * @param {Array} originalQualityAttributeValues - The original unfiltered quality attribute values array
 * @param {Object} filteredProduct - The current filtered product (used as base for other fields)
 * @param {string} marketId - The market identifier
 * @param {string} marketDate - The market date in 'fr-CA' format (YYYY-MM-DD)
 * @returns {Promise<Object>} Object containing filteredProduct and activeQualityAttributeValueIds
 */
export async function refilterProductForMarket(originalQualityAttributeValues, filteredProduct, marketId, marketDate) {
  const activeIds = await getActiveQualityAttributeValueIdsByMarket(marketId, marketDate);
  
  // Reconstruct product with original quality attribute values for filtering
  const productForFiltering = {
    ...filteredProduct,
    qualityAttributeValues: originalQualityAttributeValues
  };
  
  return {
    filteredProduct: filterProductQualityAttributeValues(productForFiltering, activeIds),
    activeQualityAttributeValueIds: activeIds
  };
}

/**
 * Handles product refiltering for market date changes with race condition protection.
 * Updates the component's filteredProduct state asynchronously.
 * @param {Object} component - The React component instance
 * @param {string} marketDate - The market date in 'fr-CA' format (YYYY-MM-DD)
 * @param {string} marketId - The market identifier
 * @returns {Promise<Object|null>} The filtered product or null if refiltering was skipped/stale
 */
export async function refilterProductForMarketDateChange(component, marketDate, marketId) {
  const { originalQualityAttributeValues, filteredProduct: currentFilteredProduct } = component.state;
  
  if (!marketDate || !originalQualityAttributeValues || !currentFilteredProduct || !marketId) {
    return null;
  }

  // Use a timestamp to track the latest refiltering request
  const refilterTimestamp = Date.now();
  component._lastRefilterTimestamp = refilterTimestamp;

  try {
    const { filteredProduct } = await refilterProductForMarket(
      originalQualityAttributeValues,
      currentFilteredProduct,
      marketId,
      marketDate
    );

    // Only update if this is still the latest refiltering request
    if (component._lastRefilterTimestamp === refilterTimestamp) {
      component.setState({ filteredProduct });
      return filteredProduct;
    }
    
    return null; // Stale request, ignore result
  } catch (ex) {
    console.error("Error refiltering product:", ex);
    // On error, keep existing filtered product to avoid breaking UI
    return null;
  }
}
